import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { Progress } from "@/components/ui/progress";
import { Upload, X, Download, Search, Trash2, RefreshCw, Play, Database } from "lucide-react";
import { uploadFileChunked, cancelUpload, UploadPausedError } from "@/lib/chunkedUpload";
import InspectPanel from "./data-files/InspectPanel";
import IngestPanel from "./data-files/IngestPanel";
import RebuildAnalyticsPanel from "./data-files/RebuildAnalyticsPanel";
import { useAuth } from "@/lib/auth";

const ALLOWED = ["zip", "csv", "xlsx", "xls", "txt"];
const MAX = 2 * 1024 ** 3;
export const fmtMB = (b) => (b < 1024 ** 2 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 ** 2).toFixed(1)} MB`);

function validate(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!file.name.includes(".") || !ALLOWED.includes(ext)) return `“.${ext}” files are not allowed. Allowed: ${ALLOWED.join(", ")}.`;
  if (file.size > MAX) return `File is ${fmtMB(file.size)} — larger than the 2 GB limit.`;
  if (file.size === 0) return "File is empty.";
  return null;
}

function Uploader({ onDone }) {
  const [st, setSt] = useState({ phase: "idle" });
  const fileRef = useRef(null);
  const ctrl = useRef(null);
  const uploadId = useRef(null);
  const inputRef = useRef(null);

  const run = async (file) => {
    ctrl.current = new AbortController();
    setSt({ phase: "uploading", loaded: 0, total: file.size, name: file.name });
    try {
      const res = await uploadFileChunked(file, {
        signal: ctrl.current.signal,
        onInit: (i) => { uploadId.current = i.upload_id; if (i.resumed && i.received.length) toast.info(`Resuming upload — ${i.received.length}/${i.total_chunks} chunks already stored`); },
        onProgress: (loaded, total) => setSt((s) => ({ ...s, loaded, total })),
      });
      setSt({ phase: "done", name: file.name, total: file.size, loaded: file.size });
      toast.success(`${res.filename} uploaded`);
      onDone();
    } catch (e) {
      if (ctrl.current?.signal.aborted) return;
      if (e instanceof UploadPausedError) setSt((s) => ({ ...s, phase: "paused", reason: e.message }));
      else setSt({ phase: "error", reason: e?.response?.data?.detail || e.message });
    }
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validate(file);
    if (err) { setSt({ phase: "rejected", reason: err }); return; }
    fileRef.current = file;
    run(file);
  };

  const cancel = async () => {
    ctrl.current?.abort();
    await cancelUpload(uploadId.current);
    setSt({ phase: "idle" });
    toast("Upload cancelled");
    onDone();
  };

  const pct = st.total ? Math.floor((st.loaded / st.total) * 100) : 0;
  const busy = st.phase === "uploading";
  return (
    <div className="bg-white border border-black/10 p-6 space-y-4" data-testid="data-files-uploader">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] kazo-text-burgundy">Upload a data file</div>
          <p className="text-sm text-neutral-600 mt-1">ZIP, CSV, XLSX, XLS or TXT · up to 2 GB · uploads in 8 MB chunks and resumes if interrupted (re-select the same file).</p>
        </div>
        <input ref={inputRef} type="file" className="hidden" accept=".zip,.csv,.xlsx,.xls,.txt" onChange={pick} data-testid="data-files-input" />
        <button className="k-btn kazo-bg-burgundy" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="data-files-select-btn">
          <Upload className="w-4 h-4" /> Select file
        </button>
      </div>
      {st.phase === "rejected" && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="data-files-reject">{st.reason}</div>}
      {st.phase === "error" && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="data-files-error">Upload failed: {st.reason}</div>}
      {["uploading", "paused", "done"].includes(st.phase) && (
        <div className="space-y-2" data-testid="data-files-progress">
          <div className="flex justify-between text-sm">
            <span className="font-medium truncate">{st.name}</span>
            <span className="font-mono text-xs" data-testid="data-files-progress-text">{pct}% · {fmtMB(st.loaded)} / {fmtMB(st.total)}</span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="flex items-center gap-2">
            {busy && <button className="k-btn k-btn-outline k-btn-sm" onClick={cancel} data-testid="data-files-cancel-btn"><X className="w-3.5 h-3.5" /> Cancel</button>}
            {st.phase === "paused" && (
              <>
                <span className="text-sm text-amber-700" data-testid="data-files-paused">Upload paused — {st.reason}</span>
                <button className="k-btn kazo-bg-burgundy k-btn-sm" onClick={() => run(fileRef.current)} data-testid="data-files-resume-btn"><Play className="w-3.5 h-3.5" /> Resume</button>
                <button className="k-btn k-btn-outline k-btn-sm" onClick={cancel}><X className="w-3.5 h-3.5" /> Cancel</button>
              </>
            )}
            {st.phase === "done" && <span className="text-sm text-green-700" data-testid="data-files-done">Upload complete</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function FileRow({ f, onInspect, onIngest, onChange }) {
  const download = async () => {
    const r = await api.post(`/data-files/${f.id}/download-link`);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
  };
  const del = async () => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    await api.delete(`/data-files/${f.id}`);
    toast.success("File deleted");
    onChange();
  };
  const ready = f.status === "Uploaded";
  return (
    <tr className="border-t border-black/5" data-testid={`data-file-row-${f.id}`}>
      <td className="py-3 px-4 font-medium">{f.filename}</td>
      <td className="px-4 font-mono text-xs">{fmtMB(f.size)}</td>
      <td className="px-4 text-xs">{f.uploaded_by_name}</td>
      <td className="px-4 text-xs">{new Date(f.completed_at || f.created_at).toLocaleString("en-IN")}</td>
      <td className="px-4 text-xs" data-testid={`data-file-status-${f.id}`}>{f.status}{f.inspection?.status === "running" ? " · inspecting…" : ""}</td>
      <td className="px-4 text-right whitespace-nowrap space-x-1">
        {ready && <button className="k-btn k-btn-outline k-btn-sm" onClick={() => onInspect(f)} data-testid={`data-file-inspect-${f.id}`}><Search className="w-3.5 h-3.5" /> Inspect</button>}
        {ready && f.ext === "zip" && <button className="k-btn k-btn-outline k-btn-sm" onClick={() => onIngest(f)} data-testid={`data-file-ingest-${f.id}`}><Database className="w-3.5 h-3.5" /> Ingest</button>}
        {ready && <button className="k-btn k-btn-outline k-btn-sm" onClick={download} data-testid={`data-file-download-${f.id}`}><Download className="w-3.5 h-3.5" /></button>}
        <button className="k-btn k-btn-outline k-btn-sm" onClick={del} data-testid={`data-file-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
}

export default function DataFilesPage() {
  const [items, setItems] = useState(null);
  const [inspecting, setInspecting] = useState(null);
  const [ingesting, setIngesting] = useState(null);
  const { user } = useAuth();
  const load = () => api.get("/data-files").then((r) => setItems(r.data.items)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load files"));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="data-files-page">
      <PageHeader title="Data Files" subtitle="DATA OPERATIONS · LARGE FILE STORAGE · INSPECT BEFORE INGEST"
        actions={<button className="k-btn k-btn-outline k-btn-sm" onClick={load} data-testid="data-files-reload"><RefreshCw className="w-3.5 h-3.5" /> Reload</button>} />
      <div className="p-8 space-y-6">
        <Uploader onDone={load} />
        <RebuildAnalyticsPanel canStart={user?.role === "super_admin"} />
        <div className="bg-white border border-black/10" data-testid="data-files-list">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 text-left">
              <tr><th className="py-3 px-4">Name</th><th className="px-4">Size</th><th className="px-4">Uploader</th><th className="px-4">Time</th><th className="px-4">Status</th><th /></tr>
            </thead>
            <tbody>
              {items?.map((f) => <FileRow key={f.id} f={f} onInspect={(x) => { setIngesting(null); setInspecting(x); }} onIngest={(x) => { setInspecting(null); setIngesting(x); }} onChange={load} />)}
              {items && items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-neutral-500" data-testid="data-files-empty">No files uploaded yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {inspecting && <InspectPanel key={inspecting.id} file={inspecting} onClose={() => { setInspecting(null); load(); }} />}
        {ingesting && <IngestPanel key={ingesting.id} file={ingesting} canStart={user?.role === "super_admin"} onClose={() => setIngesting(null)} />}
      </div>
    </div>
  );
}
